import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../library/elements/d-label';
import '../library/elements/d-action';
import '../library/elements/d-smooth-resize';
import '../library/lists/d-occurrences-list';
import { DListSection } from 'src/library/lists/d-list-section';
import { numberWord } from 'src/utilities/text';
import { t } from 'src/i18n';

import type { RelateAssetToEventOccurrencesDetail } from './relate-asset-to-event-occurrences-detail';
import type { Occurrence } from '../library/lists/occurrence';

/**
 *
 * USAGE:
 *    d-standard-event-edit
 *
 */
@customElement('d-confirmation-on-occurrences')
export class DConfirmationOnOccurrences extends LitElement {
  static readonly styles = [
    ...DListSection.styles,
    css`
      :host {
        display: block;
        position: relative;
        border-top: 1px solid var(--borderColor);
        line-height: var(--lineHeightLoose);
      }
      .header {
        position: sticky;
        top: 0;
        margin-bottom: -1px;
        border-bottom: 1px solid var(--borderColorLight);
        padding: 12px 0;
        background: white;
        z-index: 1;
      }
      .actions {
        text-align: right;
        margin-right: -8px;
        margin-bottom: -8px;
      }
      d-action {
        margin-left: 0;
      }
      d-action[plain] {
        margin: -6px -8px;
      }
      .info p {
        margin-top: 0.5em;
        margin-bottom: 0;
      }
      .info p .actions {
        white-space: nowrap;
      }
    `,
  ];
  @property({ type: Array })
  occurrences: Occurrence[] = [];
  @property({ type: Boolean })
  newItem = false;
  @property({ type: Array })
  assets: string[] = [];
  @property({ type: String })
  parentHref = '';
  @property({ type: String })
  entityType = 'event';
  @property({ type: String })
  entityName = '';
  @property({ type: Number })
  contentStickyTop = 0;

  private get label() {
    if (this.entityType === 'meeting') {
      if (this.newItem) {
        return t('occurrences.labelMeetingExists');
      }
      return t('occurrences.labelMeetingNewNameExists');
    }
    if (this.newItem) {
      return t('occurrences.labelTaskExists');
    }
    return t('occurrences.labelTaskNewNameExists');
  }

  private get occurrencesText() {
    const count = this.occurrences.length;
    const countWord = numberWord(count);
    if (this.entityType === 'meeting') {
      if (this.newItem) {
        return t('occurrences.occurrencesTextMeetingNew', { count, countWord });
      }
      return t('occurrences.occurrencesTextMeetingExisting', { count, countWord });
    }
    if (this.newItem) {
      return t('occurrences.occurrencesTextTaskNew', { count, countWord });
    }
    return t('occurrences.occurrencesTextTaskExisting', { count, countWord });
  }

  private get confirmationText() {
    if (this.entityType === 'meeting') {
      if (this.newItem) {
        return t('occurrences.confirmationCreateMeeting');
      }
      return t('occurrences.confirmationUseName');
    }
    if (this.newItem) {
      return t('occurrences.confirmationCreateTask');
    }
    return t('occurrences.confirmationUseName');
  }

  private get allRelated() {
    return (
      this.occurrences.filter((o) => {
        return o.assetSelected === true || o.assetRelated === true;
      }).length === this.occurrences.length
    );
  }

  private get noneRelated() {
    return (
      this.occurrences.filter((o) => {
        return o.assetSelected === true || o.assetRelated === true;
      }).length === 0
    );
  }

  private get newAssetRelatedOccurrences(): string[] {
    return this.occurrences
      .filter((o) => {
        return !o.assetRelated && o.assetSelected;
      })
      .map((o) => {
        return o.occurrenceUuid;
      });
  }

  confirmNew() {
    this.fireRelateAssetEvent();
    this.dispatchEvent(
      new CustomEvent('confirm', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  cancelNew() {
    this.fireRelateAssetEvent();
    this.dispatchEvent(
      new CustomEvent('cancel', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  onAssetSelectionChanged(e) {
    e.stopPropagation();
    this.occurrences = e.detail.value;
  }

  selectAllAssets() {
    this.occurrences = this.occurrences.map((o) => {
      o.assetSelected = true;
      return o;
    });
  }

  fireRelateAssetEvent() {
    if (this.newAssetRelatedOccurrences.length) {
      this.dispatchEvent(
        new CustomEvent<RelateAssetToEventOccurrencesDetail>('relate-asset-to-event-occurrences', {
          bubbles: true,
          composed: true,
          detail: {
            asset: this.assets[0],
            eventOccurrences: this.newAssetRelatedOccurrences,
          },
        }),
      );
    }
  }

  renderConditionalInfo() {
    if (this.assets.length) {
      return this.renderConditionalInfoForAsset();
    } else {
      return this.renderConditionalInfoNoAssets();
    }
  }

  render() {
    return html`
      <div class="header" style="top: ${this.contentStickyTop}px">
        <d-label allow-wrap .label=${this.label}></d-label>
        <d-smooth-resize><div class="info">${this.renderConditionalInfo()}</div></d-smooth-resize>
      </div>
      <d-occurrences-list
        .entityName=${this.entityName}
        .occurrences=${this.occurrences}
        .assets=${this.assets}
        .parentHref=${this.parentHref}
        @asset-selection-changed=${(e) => this.onAssetSelectionChanged(e)}
      ></d-occurrences-list>
    `;
  }

  private renderConditionalInfoNoAssets() {
    return html`
      <p>${this.occurrencesText}</p>
      <div class="actions">
        <d-action @click=${() => this.cancelNew()}>${t('occurrences.cancelNo')}</d-action>
        <d-action @click=${() => this.confirmNew()}>${this.confirmationText}</d-action>
      </div>
    `;
  }

  private renderConditionalInfoForAsset() {
    if (this.occurrences.length === 1) {
      if (this.allRelated) {
        return html`
          <p>${t('occurrences.assetSingleAllRelatedText')}</p>
          <div class="actions">
            <d-action @click=${() => this.cancelNew()}>${t('occurrences.cancelNo')}</d-action>
            <d-action @click=${() => this.confirmNew()}>${t('occurrences.confirmRepeatTask')}</d-action>
          </div>
        `;
      }
      return html`
        <p>
          ${t('occurrences.assetSingleNotRelatedText')}
          <d-action @click=${this.selectAllAssets}>${t('occurrences.relateToAsset')}</d-action>
        </p>
        <p>${t('occurrences.repeatTaskQuestion')}</p>
        <div class="actions">
          <d-action @click=${() => this.cancelNew()}>${t('occurrences.cancelNo')}</d-action>
          <d-action @click=${() => this.confirmNew()}>${t('occurrences.confirmRepeatTask')}</d-action>
        </div>
      `;
    } else {
      const countWord = numberWord(this.occurrences.length);
      if (this.allRelated) {
        const quantifier =
          this.occurrences.length === 2 ? t('occurrences.quantifierBoth') : t('occurrences.quantifierAll');
        return html`
          <p>${t('occurrences.assetAllRelatedText', { countWord, quantifier })}</p>
          <p>${t('occurrences.createNewRecurrenceQuestion')}</p>
          <div class="actions">
            <d-action @click=${() => this.cancelNew()}>${t('occurrences.cancelNo')}</d-action>
            <d-action @click=${() => this.confirmNew()}>${t('occurrences.confirmCreateNewRecurrence')}</d-action>
          </div>
        `;
      }
      if (this.noneRelated) {
        return html`
          <p>
            ${t('occurrences.assetNoneRelatedText', { countWord })}
            <span><d-action plain @click=${this.selectAllAssets}>${t('occurrences.relateToAll')}</d-action></span>
          </p>
          <p>${t('occurrences.createNewRecurrenceQuestion')}</p>
          <div class="actions">
            <d-action @click=${() => this.cancelNew()}>${t('occurrences.cancelNo')}</d-action>
            <d-action @click=${() => this.confirmNew()}>${t('occurrences.confirmCreateNewRecurrence')}</d-action>
          </div>
        `;
      }
      return html`
        <p>
          ${t('occurrences.assetSomeRelatedText', { countWord })}
          <d-action plain @click=${this.selectAllAssets}>${t('occurrences.relateToAll')}</d-action>
        </p>
        <p>${t('occurrences.createNewRecurrenceQuestion')}</p>
        <div class="actions">
          <d-action @click=${() => this.cancelNew()}>${t('occurrences.cancelNo')}</d-action>
          <d-action @click=${() => this.confirmNew()}>${t('occurrences.confirmCreateNewRecurrence')}</d-action>
        </div>
      `;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-confirmation-on-occurrences': DConfirmationOnOccurrences;
  }
}
